/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Your family's own notes on a recipe.
 *
 * These NEVER leave the household. They aren't copied when the recipe is
 * shared, and no other family can read them — which is the whole reason this
 * panel exists as its own thing rather than being folded into the cross-
 * household discussion (RecipeThreadPanel).
 *
 * The two sit next to each other on the recipe page on purpose. A user deciding
 * where to write "Maya wouldn't touch this" should be able to see both labels
 * at once and pick correctly, rather than remembering a rule.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { RecipesApi } from "../../api/endpoints.ts";
import { useToast } from "../../components/Toast.tsx";

export function RecipeNotesPanel({ recipeId }: { recipeId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState("");

  const notes = useQuery({
    queryKey: ["recipes", recipeId, "comments"],
    queryFn: () => RecipesApi.comments(recipeId),
  });

  const invalidate = () =>
    void qc.invalidateQueries({ queryKey: ["recipes", recipeId, "comments"] });

  const add = useMutation({
    mutationFn: (body: string) => RecipesApi.comment(recipeId, body),
    onSuccess: () => { setDraft(""); invalidate(); },
    onError: (e: Error) => toast(e.message, "danger"),
  });

  const remove = useMutation({
    mutationFn: (id: string) => RecipesApi.deleteComment(id),
    onSuccess: invalidate,
    onError: (e: Error) => toast(e.message, "danger"),
  });

  const items = notes.data?.comments ?? [];

  return (
    <section className="card">
      <header className="border-b border-rule-soft px-5 py-3">
        <h3 className="font-medium text-ink">Your family's notes</h3>
        {/* The counterpart line in RecipeThreadPanel says the opposite. Both
            are stated, so neither has to be remembered. */}
        <p className="mt-0.5 text-xs text-ink-3">
          Only your household can see these. They aren't shared when you send this recipe.
        </p>
      </header>

      <ul className="divide-y divide-rule-soft">
        {items.length === 0 && !notes.isLoading && (
          <li className="px-5 py-6 text-center text-sm text-ink-3">
            No notes yet. What would you change next time?
          </li>
        )}
        {items.map((c) => (
          <li key={c.id} className="px-5 py-3">
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-medium text-ink">{c.authorName}</span>
              <span className="text-xs text-ink-3">
                {new Date(c.createdAt).toLocaleDateString()}
              </span>
              {/* Household members share everything else, but a note is
                  attributed speech — only its author removes it. */}
              {c.isMine && (
                <button
                  type="button"
                  className="ml-auto text-xs text-ink-3 hover:text-danger"
                  onClick={() => remove.mutate(c.id)}
                >
                  Delete
                </button>
              )}
            </div>
            <p className="mt-1 whitespace-pre-wrap text-sm text-ink-2">{c.body}</p>
          </li>
        ))}
      </ul>

      <div className="border-t border-rule-soft p-4">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          maxLength={4000}
          rows={2}
          placeholder="Halved the chili. Kids ate it."
          className="input w-full resize-y"
        />
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            className="btn-ghost"
            disabled={add.isPending || !draft.trim()}
            onClick={() => add.mutate(draft.trim())}
          >
            Add note
          </button>
        </div>
      </div>
    </section>
  );
}
